import { useEffect, useState } from 'react';
import { PriorityFeeLevels } from '@shared/types/services/enums';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { Button } from '@/components/ui/button';
import { useSwap } from '@/contexts/SwapProvider';
import SlidingModal from '@/components/home/mobile/SlidingModal';
import classNames from '@/lib/misc/classNames';
import { Input } from '@/components/ui/input';
export default function SwapSettings({
  isModalOpen,
  onClose
}: {
  isModalOpen?: boolean;
  onClose?: () => void;
}) {
  const {
    fromConfig,
    setFromConfig
  } = useSwap();
  const [slippageValue, setSlippageValue] = useState(5);
  const [isAutoSlippage, setIsAutoSlippage] = useState(true);
  const [gasValue, setGasValue] = useState<PriorityFeeLevels | null>(null);
  const [customGasValue, setCustomGasValue] = useState<string>('');
  const getColorForValue = (val: number) => {
    const normalizedValue = (val - 0.5) / (20 - 0.5);
    const colors = ['#36df85', '#fc8e5f', '#e8246c'];
    const colorIndex = Math.min(Math.floor(normalizedValue * 2), 1);
    const colorRatio = normalizedValue * 2 - colorIndex;
    return interpolateColor(colors[colorIndex], colors[colorIndex + 1], colorRatio);
  };
  const interpolateColor = (color1: string, color2: string, ratio: number) => {
    const r1 = parseInt(color1.substring(1, 3), 16);
    const g1 = parseInt(color1.substring(3, 5), 16);
    const b1 = parseInt(color1.substring(5, 7), 16);
    const r2 = parseInt(color2.substring(1, 3), 16);
    const g2 = parseInt(color2.substring(3, 5), 16);
    const b2 = parseInt(color2.substring(5, 7), 16);
    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };
  const sliderColor = getColorForValue(slippageValue);
  const saveSettingsAndClose = () => {
    setFromConfig({
      ...fromConfig,
      slippage: isAutoSlippage ? 'auto' : slippageValue,
      gas: customGasValue.length ? parseFloat(customGasValue.replace(',', '.')) : gasValue ?? PriorityFeeLevels.MEDIUM
    });
    onClose();
  };
  useEffect(() => {
    if (fromConfig.slippage === 'auto') {
      setIsAutoSlippage(true);
    } else {
      setIsAutoSlippage(false);
      setSlippageValue(fromConfig.slippage);
    }
    setGasValue(fromConfig.gas ?? PriorityFeeLevels.MEDIUM);
    if (Object.values(PriorityFeeLevels).includes(fromConfig.gas)) {
      setCustomGasValue('');
    } else {
      setCustomGasValue(fromConfig.gas?.toString());
    }
  }, [fromConfig]);
  return <SlidingModal heightFit isOpen={isModalOpen} onClose={saveSettingsAndClose} data-sentry-element="SlidingModal" data-sentry-component="SwapSettings" data-sentry-source-file="SwapSettings.tsx">
      <div className='flex flex-col gap-8 px-4 pb-6'>
        <div className='flex flex-col gap-4 px-2'>
          <div className='flex flex-col gap-2'>
            <div className='flex items-center justify-between'>
              <div className='font-medium text-[16px] text-genius-cream'>Slippage</div>
              <div className='flex items-center gap-2'>
                <div className={classNames('text-genius-cream/50 bg-genius-blue px-1.5 py-0.5 rounded-sm text-[11px] font-medium transition-colors', {
                'bg-white text-genius-indigo': !isAutoSlippage
              })} onClick={() => {
                setIsAutoSlippage(false);
                setSlippageValue(5);
              }}>
                  Manual {!isAutoSlippage ? `: ${slippageValue}%` : ''}
                </div>
                <div className={classNames('text-genius-cream/50 bg-genius-blue px-1.5 py-0.5 rounded-sm text-[11px] font-medium transition-colors', {
                'bg-white text-genius-indigo': isAutoSlippage
              })} onClick={() => {
                setIsAutoSlippage(true);
              }}>
                  Auto
                </div>
              </div>
            </div>
            <div className='text-genius-cream/50 text-[12px]'>
              This setting will persist through your trading session
            </div>
          </div>
          {!isAutoSlippage ? <Slider value={slippageValue} onChange={val => setSlippageValue(val as number)} min={0.5} max={20} step={0.5} styles={{
          track: {
            height: 4,
            backgroundColor: sliderColor
          },
          handle: {
            borderColor: sliderColor,
            backgroundColor: sliderColor,
            boxShadow: 'none',
            opacity: 1,
            transform: 'scale(1.3)'
          },
          rail: {
            backgroundColor: '#e9e9e9',
            height: 4
          }
        }} /> : null}
        </div>

        <div className='flex flex-col gap-4 px-2'>
          <div className='flex flex-col gap-1.5'>
            <div className='font-medium text-[16px] text-genius-cream'>Gas</div>
            <div className='text-genius-cream/50 text-[12px]'>
              The higher the gas fee, the higher the likelihood your transaction will succeed.
            </div>
          </div>
          <div className='flex items-center gap-2 border border-genius-blue rounded-md p-2'>
            <div className={classNames('flex-1 rounded-md p-2 text-[12px] font-medium text-center transition-colors', {
            'bg-genius-blue': gasValue === PriorityFeeLevels.LOW
          })} onClick={() => {
            setGasValue(PriorityFeeLevels.LOW);
            setCustomGasValue('');
          }}>
              Medium
            </div>
            <div className={classNames('flex-1 rounded-md p-2 text-[12px] font-medium text-center transition-colors', {
            'bg-genius-blue': gasValue === PriorityFeeLevels.MEDIUM
          })} onClick={() => {
            setGasValue(PriorityFeeLevels.MEDIUM);
            setCustomGasValue('');
          }}>
              High
            </div>
            <div className={classNames('flex-1 rounded-md p-2 text-[12px] font-medium text-center transition-colors', {
            'bg-genius-blue': gasValue === PriorityFeeLevels.HIGH
          })} onClick={() => {
            setGasValue(PriorityFeeLevels.HIGH);
            setCustomGasValue('');
          }}>
              Aggressive
            </div>
            <div className='flex-1 flex items-center border border-genius-blue rounded-md p-2 text-[12px] font-medium'>
              <Input inputMode='numeric' value={customGasValue} onChange={e => {
              const inputValue = e.target.value;
              const regex = /^[0-9]*([.,][0-9]*)?$/;
              if (regex.test(inputValue)) {
                setCustomGasValue(inputValue);
                setGasValue(null);
              }
            }} className='w-full h-full bg-transparent border-none text-genius-cream/50 text-[16px] text-center p-0' data-sentry-element="Input" data-sentry-source-file="SwapSettings.tsx" />
              <span>USD</span>
            </div>
          </div>
        </div>
        <Button onClick={saveSettingsAndClose} className='w-full text-genius-indigo flex-1 min-h-12 h-12 rounded-xl' variant='pink' data-sentry-element="Button" data-sentry-source-file="SwapSettings.tsx">
          Confirm
        </Button>
      </div>
    </SlidingModal>;
}